import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CenteredSpinner } from '@components/ui/feedback';
import { StartGroupSection } from '@features/organizations/components/StartGroupSection';
import { useOrganizationAccess } from '@features/organizations/store/hooks';
import { useBundlingEnabled } from '@features/subscriptions';
import { useFeatureFlag, useGroupUserRoles } from '@features/user';
import { Box, Card, CardContent, Container, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useFeatureGate } from '@services/statsig';
import { hasBillingAccess } from '@utils/roleUtils';

import { useStickyHeaderDetection } from '../../hooks/useStickyHeaderDetection';

/**
 * Layout component for /practice/* routes
 * Handles authentication, permissions, and provides shared header with tabs
 */
export const PracticeLayout = () => {
  const theme = useTheme();
  const { userIsInGroup, userIsBillingAdmin, orgLoading } = useOrganizationAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasDataSecurity } = useBundlingEnabled();
  const groupUserRoles = useGroupUserRoles();
  const hasBillingFeature = useFeatureGate('jotbill');
  const stripeConnectFeatureFlag = useFeatureFlag('stripe_connect');

  // Sticky header detection
  const { registerSentinel, registerHeader, isStuck } = useStickyHeaderDetection();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerId = 'sticky-header-practice-layout';

  // Register sentinel after the component renders the actual content (not early returns)
  // Dependencies include states that determine early returns, so effect re-runs when content renders
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const header = headerRef.current;

    if (sentinel) {
      registerSentinel(headerId, sentinel);
    }
    if (header) {
      registerHeader(headerId, header);
    }

    return () => {
      registerSentinel(headerId, null);
      registerHeader(headerId, null);
    };
  }, [userIsInGroup, orgLoading, userIsBillingAdmin, registerSentinel, registerHeader]);

  const headerIsStuck = isStuck(headerId);

  // User is not part of any organization
  if (!userIsInGroup) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto', minHeight: '100%' }}>
        <StartGroupSection />
      </Box>
    );
  }

  // Loading organization data
  if (orgLoading) {
    return <CenteredSpinner message="Loading organization data..." />;
  }

  // User doesn't have billing admin access
  if (!userIsBillingAdmin) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          Your Practice
        </Typography>
        <Card>
          <CardContent>
            <Typography>
              You don&apos;t have permission to manage this organization. Contact your billing
              administrator for access.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Determine current tab based on pathname
  const currentTab = location.pathname;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sentinel element for sticky detection */}
      <div
        ref={sentinelRef}
        data-header-id={headerId}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Sticky Header with Tabs */}
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              pt: 3,
              pb: 0,
            }}
          >
            {/* Title and Subtitle */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                Your Practice
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage your organization
              </Typography>
            </Box>

            {/* Tabs Navigation */}
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 48,
                  fontSize: '0.9375rem',
                },
                boxShadow: headerIsStuck ? '0 2px 4px 0 rgba(0, 0, 0, 0.08)' : 'none',
                transition: headerIsStuck ? 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                clipPath: headerIsStuck ? 'inset(0px 0 -10px 0)' : 'inset(0 0 0 0)',
              }}
            >
              <Tab label="Clinicians" value="/practice/clinicians" />
              <Tab label="Your Subscription" value="/practice/subscriptions" />
              {hasDataSecurity && <Tab label="Data & Security" value="/practice/data-security" />}
              {hasBillingFeature && hasBillingAccess(groupUserRoles) && (
                <Tab label="Billing & Claims" value="/practice/billing-settings" />
              )}
              <Tab label="Payer Docs Center" value="/practice/payer-docs-center" />
              {stripeConnectFeatureFlag && (
                <Tab label="Patient Payments" value="/practice/payments" />
              )}
              <Tab label="Practice Settings" value="/practice/settings" />
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{ py: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Outlet />
      </Container>
    </Box>
  );
};
